import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Eyebrow, FadeUp, MaskLine } from "./Reveal";
import { InquireDialog } from "./InquireDialog";

const BASE = [
  { id: "cqb-1", code: "CQB-I", img: "/img/fundamentals.jpg", pos: "50% 45%" },
  { id: "cqb-2", code: "CQB-II", img: "/img/op-team.jpg" },
  { id: "cqb-3", code: "CQB-III", img: "/img/lowlight.jpg", pos: "50% 55%" },
  { id: "cqb-4", code: "FOF", img: "/img/fof-team.jpg", pos: "50% 42%" },
];

export const Courses = () => {
  const { t } = useLang();
  const { img } = useSite();
  const [inquire, setInquire] = useState(null);
  const COURSES = BASE.map((c, i) => {
    const [title, tag, desc] = t("courses.list")[i];
    const custom = img(`course-${c.id}`);
    return { ...c, title, tag, desc, img: custom, pos: custom === c.img ? c.pos : undefined };
  });

  return (
    <section id="courses" data-testid="cqb-courses-section" className="relative px-5 sm:px-8 lg:px-12 py-24 sm:py-36">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <Eyebrow>{t("courses.eyebrow")}</Eyebrow>
          <h2 className="font-display font-black uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.85] mt-5">
            <MaskLine>{t("courses.title")[0]}</MaskLine>
            <MaskLine delay={0.1}>{t("courses.title")[1]}<span className="text-[var(--signal)]">.</span></MaskLine>
          </h2>
        </div>
        <FadeUp className="max-w-sm text-sm sm:text-base text-[var(--muted)] leading-relaxed">
          {t("courses.intro")}
        </FadeUp>
      </div>

      <FadeUp className="mb-10 clip-cut-sm border border-[var(--line)] bg-[var(--ink-2)] px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6" >
        <span data-testid="gbb-badge" className="self-start font-mono text-[11px] uppercase tracking-[0.25em] bg-white text-black px-3 py-1.5 whitespace-nowrap">{t("courses.gbbTitle")}</span>
        <p className="text-sm sm:text-base text-[var(--bone)]">{t("courses.gbb")}</p>
      </FadeUp>

      <div className="border-t border-[var(--line)]">
        {COURSES.map((c, i) => (
          <FadeUp key={c.id} delay={i * 0.06}>
            <button
              data-testid={`course-row-${c.id}`}
              onClick={() => setInquire(c)}
              className="group w-full text-left grid grid-cols-12 gap-4 items-center py-7 sm:py-9 border-b border-[var(--line)] relative overflow-hidden"
            >
              <span className="absolute inset-0 bg-[var(--ink-3)] origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)]" />
              <span className="relative col-span-3 sm:col-span-2 lg:col-span-1 font-mono text-xs text-[var(--signal)]">{c.code}</span>
              <span className="relative col-span-9 sm:col-span-10 lg:col-span-4 font-display font-extrabold uppercase text-3xl sm:text-5xl group-hover:translate-x-3 transition-transform duration-500">{c.title}</span>
              <span className="relative col-span-12 lg:col-span-2 overflow-hidden clip-cut-sm">
                <img src={c.img} alt={c.title} style={{ objectPosition: c.pos || "50% 50%" }} className="w-full h-44 lg:h-28 object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700" />
              </span>
              <span className="relative col-span-12 sm:col-span-11 lg:col-span-4 text-sm text-[var(--muted)] leading-relaxed">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone)] block mb-1.5">{c.tag}</span>
                {c.desc}
              </span>
              <span className="relative hidden sm:flex col-span-1 justify-end">
                <ArrowUpRight className="w-7 h-7 group-hover:text-[var(--signal)] group-hover:rotate-45 transition-[transform,color] duration-500" />
              </span>
            </button>
          </FadeUp>
        ))}
      </div>
      <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[var(--muted)]">{t("courses.note")}</p>

      <InquireDialog
        open={!!inquire}
        onOpenChange={(o) => !o && setInquire(null)}
        type="course"
        subject={inquire ? `${inquire.code} — ${inquire.title}` : ""}
      />
    </section>
  );
};
